import React, { useState, useRef, useContext } from "react";
import { ThemeContext } from "styled-components";

const OTPInputField = ({
    verificationCode = '',
    onChangeAuthForm,
    name = 'verificationCode',
    disableKeeper = false,
    disabled = false,
    sx = {}
}) => {
    const inputsRef = useRef([]);
    const [hover, setHover] = useState(false);
    const [anyFocused, setAnyFocused] = useState(false);
    const theme = useContext(ThemeContext);

    // Ensure verificationCode is always a string
    const safeVerificationCode = verificationCode || '';

    const handleInputChange = (index, event) => {
        const value = event.target.value;

        // Extract only the last character if multiple characters are entered
        const lastChar = value.slice(-1);

        // Only process if it's a valid digit or empty
        if (lastChar === "" || /^[0-9]$/.test(lastChar)) {
            // Update the verification code with just the last character
            const newValue = safeVerificationCode.substring(0, index) + lastChar + safeVerificationCode.substring(index + 1);

            const fakeEvent = {
                target: {
                    name: name,
                    value: newValue
                }
            };

            onChangeAuthForm(fakeEvent);

            // Auto-advance to next field when a digit is entered
            if (lastChar !== "" && index < 5) {
                requestAnimationFrame(() => {
                    const nextInput = inputsRef.current[index + 1];
                    if (nextInput) {
                        nextInput.focus();
                    }
                });
            }
        } else if (/^[0-9]+$/.test(value)) {
            // Handle multiple digits (paste operation)
            const digits = value.split('');
            let newValue = safeVerificationCode;

            // Update each digit starting from current index
            for (let i = 0; i < digits.length && (index + i) < 6; i++) {
                newValue = newValue.substring(0, index + i) + digits[i] + newValue.substring(index + i + 1);
            }

            const fakeEvent = {
                target: {
                    name: name,
                    value: newValue
                }
            };

            onChangeAuthForm(fakeEvent);

            // Focus the next empty field or the last field if all are filled
            const nextEmptyIndex = Math.min(index + digits.length, 5);
            if (nextEmptyIndex < 6) {
                requestAnimationFrame(() => {
                    const nextInput = inputsRef.current[nextEmptyIndex];
                    if (nextInput) {
                        nextInput.focus();
                    }
                });
            }
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace") {
            const currentFieldValue = safeVerificationCode[index] || '';

            if (currentFieldValue) {
                // If current field has content, clear it and stay in current field
                const updatedValue = safeVerificationCode.substring(0, index) + '' + safeVerificationCode.substring(index + 1);
                const fakeEvent = {
                    target: {
                        name: name,
                        value: updatedValue
                    }
                };
                onChangeAuthForm(fakeEvent);
            } else if (index > 0) {
                // If current field is empty, move to previous field and clear it
                const updatedValue = safeVerificationCode.substring(0, index - 1) + '' + safeVerificationCode.substring(index);
                const fakeEvent = {
                    target: {
                        name: name,
                        value: updatedValue
                    }
                };
                onChangeAuthForm(fakeEvent);
                inputsRef.current[index - 1].focus();
            }
            event.preventDefault();
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const pasteData = event.clipboardData.getData('text').slice(0, 6); // Get the clipboard data and slice to 6 characters

        // Filter out non-numeric characters
        const numericData = pasteData.replace(/[^0-9]/g, '');

        if (numericData.length === 0) {
            return; // No valid numeric characters to paste
        }

        const newVerificationCode = safeVerificationCode.split('');

        // Assign pasted data to the verification code based on current focus
        let selectedIndex = inputsRef.current.findIndex(input => input === document.activeElement);
        numericData.split('').forEach((char, index) => {
            const position = selectedIndex + index;
            if (position < 6) {
                newVerificationCode[position] = char;
                if (position < 5) {
                    inputsRef.current[position + 1].focus();
                }
            }
        });

        const fakeEvent = {
            target: {
                name: name,
                value: newVerificationCode.join('')
            }
        };
        onChangeAuthForm(fakeEvent);
    };

    return (
        <>
            <style>
                {`
                    .otp-input-field input {
                        border-color: ${theme.colors.secondary} !important;
                        border-width: 1px !important;
                        opacity: 1 !important;
                        color: ${theme.colors.text} !important;
                        background-color: ${theme.colors.backgroundPrimary} !important;
                    }
                    .otp-input-field:hover input {
                        border-color: ${theme.colors.text} !important;
                        border-width: 1px !important;
                        opacity: 1 !important;
                    }
                    .otp-input-field.focused input {
                        border-color: ${theme.colors.text} !important;
                        border-width: 1px !important;
                        opacity: 1 !important;
                    }
                    .otp-input-field input:focus {
                        border-color: ${theme.colors.text} !important;
                        border-width: 1px !important;
                        outline: 0;
                        opacity: 1 !important;
                    }
                    .otp-input-field input:disabled {
                        border-color: ${theme.colors.borderDisabled} !important;
                        color: ${theme.colors.textDisabled} !important;
                        opacity: 0.6 !important;
                    }
                `}
            </style>
            <div
                className={`otp-input-field ${anyFocused ? 'focused' : ''}`}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flex: 1,
                    position: 'relative'
                }}
                onMouseOver={() => setHover(true)}
                onMouseOut={() => setHover(false)}
            >
            {Array.from({ length: 6 }).map((_, index) => (
                <input
                    key={index}
                    type={disableKeeper ? "text" : "tel"}
                    autoComplete={disableKeeper ? "off" : "one-time-code"}
                    inputMode="numeric"
                    maxLength="6"
                    value={safeVerificationCode[index] || ""}
                    onChange={e => handleInputChange(index, e)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    onFocus={e => {
                        setAnyFocused(true);
                        e.target.select();
                    }}
                    onBlur={() => {
                        // Wait a tick to allow focus to move to another input in the group
                        setTimeout(() => {
                            const active = document.activeElement;
                            const stillInside = inputsRef.current.some((el) => el === active);
                            if (!stillInside) {
                                setAnyFocused(false);
                            }
                        }, 0);
                    }}
                    disabled={disabled}
                    ref={el => inputsRef.current[index] = el}
                    {...(disableKeeper ? {
                        "data-keeper-ignore": "true",
                        "data-password-manager": "disabled",
                        "data-form-type": "other",
                        "data-lpignore": "true",
                        "data-1p-ignore": "true",
                        "data-bwignore": "true",
                        "data-dashlane-ignore": "true",
                        "data-bitwarden-ignore": "true"
                    } : {
                        "data-keeper-group": "otp",
                        "data-keeper-field": `otp-${index}`
                    })}
                        style={{
                            width: 'calc(100% / 6)',
                            height: '40px',
                            textAlign: 'center',
                            marginRight: index < 5 ? '8px' : '0px',
                            borderRadius: '4px',
                            fontSize: '1rem', // Minimum 16px (1rem) to prevent iOS zoom, scales with screen size
                        }}
                />
            ))}
            </div>
        </>
    );
};

export default OTPInputField;
